import type { AxiosInstance } from 'axios';
import type { ClientState } from '../types/state';

/**
 * Process queued requests after token refresh completes.
 *
 * This function is called after a token refresh succeeds or fails.
 * It processes all requests that were queued while the refresh was in progress.
 *
 * If the refresh succeeded:
 * - Updates each request with the new token
 * - Retries each request with the authenticated axios instance
 * - Resolves/rejects the original promise based on retry result
 *
 * If the refresh failed:
 * - Rejects all queued requests with the error
 *
 * After processing, clears the request queue.
 *
 * @param state Client state containing the request queue
 * @param authenticatedInstance Axios instance for retrying requests
 * @param error Error from token refresh (if failed), null if succeeded
 * @param newToken New access token (if refresh succeeded)
 *
 * @example
 * ```typescript
 * // After successful token refresh
 * processQueue(state, authenticatedInstance, null, newToken);
 *
 * // After failed token refresh
 * processQueue(state, authenticatedInstance, error);
 * ```
 */
export function processQueue(
  state: ClientState,
  authenticatedInstance: AxiosInstance,
  error: Error | null,
  newToken?: string
): void {
  state.requestQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      // Token refresh failed - reject all queued requests
      reject(error);
    } else if (newToken) {
      // Token refresh succeeded - retry each request with new token
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newToken}`;
      authenticatedInstance(config).then(resolve).catch(reject);
    } else {
      // Should not happen - no error but no token either
      reject(new Error("No token available after refresh"));
    }
  });

  // Clear the queue after processing
  state.requestQueue = [];
}
